import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import Logo from "../Logo";
import { apiFetch, subscribeAccessTokenRefreshed, readCachedWebUser, shouldPreserveWebSessionOn401 } from "../../lib/webApi";
import { UserSessionContext } from "../../contexts/UserSessionContext";
import { buildSessionExpiredState, redirectToSessionExpiredLogin } from "../../lib/sessionExpiry";
import {
  COLD_LOAD_SPINNER_MAX_MS,
  armColdLoadDeadline,
  readWebAuthBootState,
} from "../../lib/coldLoadDeadline";
import { WWW_PAGE_SHELL_MIN_H_CLASS } from "../../lib/wwwViewportShell";
import {
  clearOnboardingJustCompletedFlag,
  isWebOnboardingComplete,
  mergeOnboardingCompletedAt,
  readOnboardingJustCompletedFlag,
  shouldRedirectToOnboarding,
} from "../../lib/webOnboardingGate";

const AUTH_CHECK_TIMEOUT_MS = COLD_LOAD_SPINNER_MAX_MS;
const AUTH_UNREACHABLE_MESSAGE =
  "Could not reach server. Is the backend running? Use the app at http://localhost:3000.";

function AuthGuard({ children }) {
  const [boot] = useState(() =>
    readWebAuthBootState({ getCachedUser: readCachedWebUser })
  );
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    boot.kind === "cached" ? true : boot.kind === "guest" ? false : null
  );
  const [sessionUser, setSessionUser] = useState(() =>
    boot.kind === "cached" ? boot.user : null
  );
  const [isLoading, setIsLoading] = useState(() => boot.kind === "pending");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const authCheckIdRef = useRef(0);
  const hasResolvedInitialCheckRef = useRef(boot.kind === "cached");
  const isAuthenticatedRef = useRef(boot.kind === "cached");
  // Read via ref (not a checkAuthStatus dep) so navigating between routes does
  // not change checkAuthStatus's identity and re-trigger a full /api/auth/me
  // round-trip on every SPA route change.
  const locationRef = useRef(location);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated === true;
  }, [isAuthenticated]);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const markLoggedOut = useCallback(() => {
    setSessionUser(null);
    setIsAuthenticated(false);
  }, []);

  const handleExpiredSession = useCallback(() => {
    redirectToSessionExpiredLogin({
      navigate,
      location: locationRef.current,
      onBeforeNavigate: markLoggedOut,
    });
  }, [markLoggedOut, navigate]);

  const checkAuthStatus = useCallback(async () => {
    const requestId = ++authCheckIdRef.current;
    const isBackgroundValidation =
      hasResolvedInitialCheckRef.current && isAuthenticatedRef.current;
    const applyIfCurrent = (fn) => {
      if (authCheckIdRef.current !== requestId) return false;
      fn();
      return true;
    };

    let releaseDeadline = () => {};

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        applyIfCurrent(markLoggedOut);
        applyIfCurrent(() => {
          setError("");
          setIsLoading(false);
        });
        return;
      }

      if (!isBackgroundValidation) {
        releaseDeadline = armColdLoadDeadline(() => {
          applyIfCurrent(() => {
            hasResolvedInitialCheckRef.current = true;
            setError(AUTH_UNREACHABLE_MESSAGE);
            setIsAuthenticated(false);
            setIsLoading(false);
          });
        });
      }

      applyIfCurrent(() => {
        setError("");
        if (!isBackgroundValidation) {
          setIsLoading(true);
        }
      });

      // Bust browser cache if user just completed onboarding to avoid stale
      // onboardingCompletedAt=null causing a redirect loop back to /onboarding
      const bustCache = readOnboardingJustCompletedFlag();
      const mePath = bustCache
        ? `/api/auth/me?_t=${Date.now()}`
        : "/api/auth/me";
      const extraHeaders = bustCache ? { "Cache-Control": "no-cache" } : {};

      let response;
      try {
        response = await apiFetch(mePath, {
          token,
          timeoutMs: AUTH_CHECK_TIMEOUT_MS,
          headers: extraHeaders,
          skipResponseCache: bustCache,
        });
      } catch (err) {
        if (err?.name === "AbortError") {
          response = { ok: false, status: 408 };
        } else {
          throw err;
        }
      }

      if (response.ok) {
        const userData = await response.json();
        const mergedUser = mergeOnboardingCompletedAt(
          userData,
          readCachedWebUser()
        );
        if (!applyIfCurrent(() => {
          localStorage.setItem("user", JSON.stringify(mergedUser));
          setSessionUser(mergedUser);
          setIsAuthenticated(true);
          setError("");
        })) {
          return;
        }

        const pathname = locationRef.current.pathname;
        // Finished riders (live /me or cached stamp) stay on app routes.
        // Do not bounce /training-plans back to Step 1.
        if (shouldRedirectToOnboarding({ user: mergedUser, pathname })) {
          navigate("/onboarding", { replace: true });
          return;
        }

        if (bustCache) clearOnboardingJustCompletedFlag();

        // Already onboarded but stuck on /onboarding? Go to dashboard.
        if (isWebOnboardingComplete(mergedUser) && pathname === "/onboarding") {
          navigate("/dashboard", { replace: true });
          return;
        }
      } else if (response.status === 401 || response.status === 403) {
        if (authCheckIdRef.current !== requestId) return;
        if (response.status === 401 && (await shouldPreserveWebSessionOn401(response))) {
          const cachedUser = readCachedWebUser();
          if (cachedUser && applyIfCurrent(() => {
            setSessionUser(cachedUser);
            setIsAuthenticated(true);
          })) {
            return;
          }
        }
        handleExpiredSession();
        return;
      } else if (response.status === 408) {
        if (isBackgroundValidation) return;
        applyIfCurrent(() => {
          setError(AUTH_UNREACHABLE_MESSAGE);
          setIsAuthenticated(false);
        });
      } else {
        if (isBackgroundValidation) return;
        applyIfCurrent(() => {
          setError("Authentication check failed");
          setIsAuthenticated(false);
        });
      }
    } catch (err) {
      console.error("Auth check error:", err);
      const isTimeout = err?.name === "AbortError";
      const isNetwork =
        err?.message?.includes("Failed to fetch") ||
        err?.message?.includes("Network request failed");
      if (isBackgroundValidation) {
        return;
      }
      if (isTimeout || isNetwork) {
        applyIfCurrent(() => {
          setError(AUTH_UNREACHABLE_MESSAGE);
        });
      } else {
        applyIfCurrent(() => {
          setError("Authentication check failed");
        });
      }
      applyIfCurrent(() => {
        setIsAuthenticated(false);
      });
    } finally {
      releaseDeadline();
      applyIfCurrent(() => {
        hasResolvedInitialCheckRef.current = true;
        setIsLoading(false);
      });
    }
  }, [handleExpiredSession, markLoggedOut, navigate]);

  // Runs once on mount (checkAuthStatus's identity is now stable across route
  // changes) - not on every SPA navigation. See locationRef above.
  useEffect(() => {
    void checkAuthStatus();
  }, [checkAuthStatus]);

  useEffect(() => {
    return subscribeAccessTokenRefreshed(({ user: fresh }) => {
      if (!fresh) return;
      setSessionUser((prev) => {
        const next = prev
          ? {
              ...fresh,
              stats: prev.stats,
              mealPlanGenerationsRemaining: prev.mealPlanGenerationsRemaining,
              mealPlanGenerationsLimit: prev.mealPlanGenerationsLimit,
              mealPlanGenerationsUsed: prev.mealPlanGenerationsUsed,
              mealPlanWindowDays: prev.mealPlanWindowDays,
            }
          : fresh;
        localStorage.setItem("user", JSON.stringify(next));
        return next;
      });
    });
  }, []);

  if (isLoading) {
    return (
      <div
        className={`${WWW_PAGE_SHELL_MIN_H_CLASS} bg-[var(--bg-primary)] flex items-center justify-center`}
        aria-busy="true"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center space-y-6"
          role="status"
          aria-live="polite"
        >
          {/* Logo */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex justify-center"
          >
            <Logo size={72} variant="color" showText textClass="text-3xl md:text-4xl" />
          </motion.div>

          {/* Loading Animation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="space-y-4"
          >
            <p className="text-lg font-semibold text-[var(--text-primary)] text-center">
              Verifying access
            </p>

            <div className="flex justify-center gap-1.5 py-1" aria-hidden="true">
              <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse [animation-delay:0ms]" />
              <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse [animation-delay:150ms]" />
              <span className="w-2 h-2 rounded-full bg-primary-500 animate-pulse [animation-delay:300ms]" />
            </div>

            <div className="w-64 h-2 bg-[var(--bg-elevated)] rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-primary"
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 2, ease: "easeInOut" }}
              />
            </div>

            <p className="text-[var(--text-secondary)]">
              Checking your authentication status...
            </p>
          </motion.div>

          {/* Security Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="flex justify-center space-x-6"
          >
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-success-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-[var(--text-secondary)]">Secure Connection</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-[var(--text-secondary)]">Validating Session</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${WWW_PAGE_SHELL_MIN_H_CLASS} bg-[var(--bg-primary)] flex items-center justify-center p-4`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center space-y-6 max-w-md"
        >
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-[var(--text-primary)]">
              Authentication Error
            </h2>
            <p className="text-[var(--text-secondary)]">{error}</p>
          </div>

          <motion.button
            onClick={() => {
              setError("");
              setIsLoading(true);
              checkAuthStatus();
            }}
            className="btn-primary"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Try Again
          </motion.button>
        </motion.div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={buildSessionExpiredState(location)}
      />
    );
  }

  // User is authenticated, show the protected content
  return (
    <UserSessionContext.Provider value={{ user: sessionUser, setUser: setSessionUser }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {children}
      </motion.div>
    </UserSessionContext.Provider>
  );
}

export default AuthGuard;
